import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderKanban, Plus, ChevronRight, Calendar as CalIcon,
  LayoutList, Columns3, MoreHorizontal, Check, Sparkles,
} from 'lucide-react';
import { AIProjectGenerator } from '../components/ai/AIProjectGenerator';
import { T, ROLES, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { Glass } from '../components/glass/Glass';
import { Pill, RoleBadge } from '../components/primitives/Badge';
import { ProgressBar } from '../components/primitives/ProgressBar';
import { SectionHeader } from '../components/cards/SectionHeader';
import { ViewHero } from '../components/cards/ViewHero';
import { Btn } from '../components/primitives/Btn';

const STATUS_COLORS = {
  active:    { label: 'Active',    color: T.green,    bg: T.greenLight },
  on_hold:   { label: 'On Hold',   color: T.amber,    bg: T.amberLight },
  completed: { label: 'Completed', color: T.blue,     bg: T.blueLight },
  archived:  { label: 'Archived',  color: T.textMuted, bg: 'rgba(0,0,0,0.04)' },
};

const PRIORITY_MAP = {
  1: { label: 'Critical', color: T.red },
  2: { label: 'High',     color: T.amber },
  3: { label: 'Medium',   color: T.blue },
  4: { label: 'Low',      color: T.textMuted },
};

// ─── New Project Modal ──────────────────────────────────────────────────────

const NewProjectModal = ({ roles, templates, milestones, tasks, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [priority, setPriority] = useState(3);
  const [roleId, setRoleId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);

    // Create the project
    const { data: project, error } = await supabase.from('goals').insert({
      title: name.trim(),
      description: desc.trim() || null,
      priority,
      role_id: roleId || null,
      is_project: true,
      project_type: 'regular',
      status: 'active',
      type: 'qualitative',
    }).select().single();

    // If created from a template, copy stages and tasks
    if (!error && project && selectedTemplate) {
      const templateStages = milestones
        .filter(m => m.goal_id === selectedTemplate.id)
        .sort((a, b) => a.sort_order - b.sort_order);

      for (const stage of templateStages) {
        const { data: newStage } = await supabase.from('milestones').insert({
          goal_id: project.id,
          title: stage.title,
          sort_order: stage.sort_order,
          done: false,
          is_active: stage.sort_order === 0,
          stage_status: stage.sort_order === 0 ? 'active' : 'not_started',
        }).select().single();

        if (newStage) {
          const templateTasks = tasks.filter(t => t.milestone_id === stage.id);
          for (const task of templateTasks) {
            await supabase.from('tasks').insert({
              title: task.title,
              goal_id: project.id,
              milestone_id: newStage.id,
              role_id: roleId || task.role_id || null,
              priority: task.priority || 2,
              duration_minutes: task.duration_minutes || 30,
              status: 'todo',
              done: false,
              auto_schedule: true,
              sort_order: task.sort_order || 0,
            });
          }
        }
      }
    }

    if (!error) onCreated();
    setCreating(false);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }} transition={springs.gentle}
        onClick={e => e.stopPropagation()}
        style={{
          width: 480, background: '#fff', borderRadius: 16,
          padding: 28, boxShadow: T.shadowLg,
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 }}>New Project</h2>

        {/* Template selector */}
        {templates && templates.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 6, display: 'block' }}>Start from template</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => setSelectedTemplate(null)} style={{
                padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                border: `1px solid ${!selectedTemplate ? T.amberBorder : 'rgba(0,0,0,0.08)'}`,
                background: !selectedTemplate ? T.amberLight : 'transparent',
                color: !selectedTemplate ? T.amber : T.textMid,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>Blank</button>
              {templates.map(tmpl => (
                <button key={tmpl.id} onClick={() => { setSelectedTemplate(tmpl); if (!name) setName(tmpl.title + ' Copy'); }}
                  style={{
                    padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                    border: `1px solid ${selectedTemplate?.id === tmpl.id ? T.blueBorder || 'rgba(74,158,204,0.25)' : 'rgba(0,0,0,0.08)'}`,
                    background: selectedTemplate?.id === tmpl.id ? T.blueLight : 'transparent',
                    color: selectedTemplate?.id === tmpl.id ? T.blue : T.textMid,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                  {tmpl.title}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input
            value={name} onChange={e => setName(e.target.value)}
            placeholder="Project name"
            autoFocus
            style={{
              padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.10)',
              fontSize: 14, fontFamily: 'inherit', outline: 'none',
            }}
            onKeyDown={e => { if (e.key === 'Enter') create(); }}
          />

          <textarea
            value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="Description (optional)"
            rows={3}
            style={{
              padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.10)',
              fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical',
            }}
          />

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 6, display: 'block' }}>Priority</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[1, 2, 3, 4].map(p => (
                  <button key={p} onClick={() => setPriority(p)} style={{
                    flex: 1, padding: '6px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: priority === p ? PRIORITY_MAP[p].color : 'rgba(0,0,0,0.04)',
                    color: priority === p ? '#fff' : T.textMid,
                    fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                  }}>
                    {PRIORITY_MAP[p].label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 6, display: 'block' }}>Workspace</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {roles.map(r => (
                <Pill key={r.id} active={roleId === r.id} color={ROLES[r.slug]?.color} bg={ROLES[r.slug]?.light} border={ROLES[r.slug]?.border}
                  onClick={() => setRoleId(roleId === r.id ? '' : r.id)}>
                  {r.label}
                </Pill>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} style={{
            padding: '8px 18px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.10)',
            background: 'transparent', color: T.textMid, cursor: 'pointer',
            fontSize: 13, fontFamily: 'inherit',
          }}>Cancel</button>
          <button onClick={create} disabled={!name.trim() || creating} style={{
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: T.amber, color: '#fff', cursor: 'pointer',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            opacity: name.trim() && !creating ? 1 : 0.5,
          }}>Create Project</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Project Card (Kanban) ──────────────────────────────────────────────────

const ProjectCard = ({ project, milestones, tasks, onClick }) => {
  const projectMilestones = milestones.filter(m => m.goal_id === project.id);
  const projectTasks = tasks.filter(t => t.goal_id === project.id);
  const doneTasks = projectTasks.filter(t => t.done || t.status === 'done');
  const progress = projectTasks.length > 0 ? Math.round((doneTasks.length / projectTasks.length) * 100) : 0;
  const activeStage = projectMilestones.find(m => m.is_active);
  const r = project.roles?.slug ? ROLES[project.roles.slug] : null;
  const prio = PRIORITY_MAP[project.priority] || PRIORITY_MAP[3];
  const deadline = project.deadline;

  return (
    <motion.div
      whileHover={{ y: -2, boxShadow: T.shadow }}
      transition={springs.snappy}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <Glass style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.3, flex: 1 }}>{project.title}</div>
          <div style={{
            fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
            color: prio.color, background: `${prio.color}15`,
          }}>{prio.label}</div>
        </div>

        <ProgressBar value={progress} color={r?.color || T.amber} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {r && <RoleBadge roleSlug={project.roles.slug} small />}
            <span style={{ fontSize: 11, color: T.textMuted }}>
              {doneTasks.length}/{projectTasks.length} tasks
            </span>
          </div>
          {activeStage && (
            <span style={{ fontSize: 10, color: T.blue, fontWeight: 500 }}>
              {activeStage.title}
            </span>
          )}
        </div>

        {deadline && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
            <CalIcon size={10} color={T.textMuted} />
            <span style={{ fontSize: 10, color: new Date(deadline) < new Date() ? T.red : T.textMuted }}>
              {new Date(deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </span>
          </div>
        )}
      </Glass>
    </motion.div>
  );
};

// ─── List Row ───────────────────────────────────────────────────────────────

const ProjectRow = ({ project, milestones, tasks, onClick }) => {
  const projectTasks = tasks.filter(t => t.goal_id === project.id);
  const doneTasks = projectTasks.filter(t => t.done || t.status === 'done');
  const progress = projectTasks.length > 0 ? Math.round((doneTasks.length / projectTasks.length) * 100) : 0;
  const activeStage = milestones.filter(m => m.goal_id === project.id).find(m => m.is_active);
  const r = project.roles?.slug ? ROLES[project.roles.slug] : null;
  const prio = PRIORITY_MAP[project.priority] || PRIORITY_MAP[3];
  const s = STATUS_COLORS[project.status] || STATUS_COLORS.active;

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ background: 'rgba(0,0,0,0.02)' }}
      style={{
        display: 'grid', gridTemplateColumns: '1fr 90px 80px 90px 100px 60px',
        alignItems: 'center', gap: 8, padding: '10px 14px',
        borderRadius: 10, cursor: 'pointer',
        borderBottom: '1px solid rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <FolderKanban size={14} color={r?.color || T.textMuted} />
        <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{project.title}</span>
      </div>
      <div>
        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: s.bg, color: s.color }}>{s.label}</span>
      </div>
      <div>
        <span style={{ fontSize: 10, fontWeight: 600, color: prio.color }}>{prio.label}</span>
      </div>
      <div>
        {r && <RoleBadge roleSlug={project.roles.slug} small />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ProgressBar value={progress} color={r?.color || T.amber} height={4} />
        <span style={{ fontSize: 10, color: T.textMuted, flexShrink: 0 }}>{progress}%</span>
      </div>
      <div>
        <span style={{ fontSize: 10, color: T.textMuted }}>{activeStage?.title || '-'}</span>
      </div>
    </motion.div>
  );
};

// ─── Main ProjectsView ──────────────────────────────────────────────────────

export const ProjectsView = ({ goals, milestones, tasks, roles, onUpdate, onSelectProject }) => {
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'kanban'
  const [filter, setFilter] = useState('all');
  const [showNewProject, setShowNewProject] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);

  const projects = goals.filter(g => g.is_project);
  const filtered = filter === 'all'
    ? projects
    : projects.filter(p => p.roles?.slug === filter);

  const activeProjects = filtered.filter(p => p.status === 'active');
  const onHoldProjects = filtered.filter(p => p.status === 'paused' || p.project_status === 'on_hold');
  const completedProjects = filtered.filter(p => p.status === 'complete' || p.project_status === 'completed');

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <ViewHero
        icon={FolderKanban}
        gradient="linear-gradient(135deg, #f8d77a 0%, #D4921E 100%)"
        title="Projects"
        stats={[
          { label: `${activeProjects.length} active`, color: T.green, bg: 'rgba(90,158,120,0.10)', border: 'rgba(90,158,120,0.18)' },
          { label: `${onHoldProjects.length} on hold`, color: T.amber, bg: 'rgba(212,146,30,0.10)', border: 'rgba(212,146,30,0.18)' },
          { label: `${completedProjects.length} completed`, color: T.blue, bg: 'rgba(74,158,204,0.10)', border: 'rgba(74,158,204,0.18)' },
        ]}
        right={(
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', borderRadius: 999, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden', background: 'rgba(255,255,255,0.5)' }}>
              <button onClick={() => setViewMode('list')} style={{
                padding: '6px 10px', border: 'none', cursor: 'pointer',
                background: viewMode === 'list' ? 'rgba(255,255,255,0.85)' : 'transparent',
                color: viewMode === 'list' ? T.text : T.textMuted, fontFamily: 'inherit',
              }}><LayoutList size={14} /></button>
              <button onClick={() => setViewMode('kanban')} style={{
                padding: '6px 10px', border: 'none', cursor: 'pointer',
                background: viewMode === 'kanban' ? 'rgba(255,255,255,0.85)' : 'transparent',
                color: viewMode === 'kanban' ? T.text : T.textMuted, fontFamily: 'inherit',
              }}><Columns3 size={14} /></button>
            </div>
            <button onClick={() => setShowAIGenerator(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #D4921E, #C86878)', color: '#fff',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              boxShadow: '0 4px 12px rgba(200,104,120,0.25)',
            }}>
              <Sparkles size={14} /> AI Generate
            </button>
            <button onClick={() => setShowNewProject(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 10, border: 'none',
              background: T.amber, color: '#fff', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              boxShadow: '0 4px 12px rgba(212,146,30,0.30)',
            }}>
              <Plus size={14} /> New Project
            </button>
          </div>
        )}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['all', ...Object.keys(ROLES)].map(f => {
          const r = ROLES[f];
          const count = f === 'all' ? projects.length : projects.filter(p => p.roles?.slug === f).length;
          return (
            <Pill key={f} active={filter === f} color={r?.color} bg={r?.light} border={r?.border} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : r.label}
              {count > 0 && <span style={{ marginLeft: 5, opacity: 0.65, fontSize: 11 }}>{count}</span>}
            </Pill>
          );
        })}
      </div>

      {/* Empty state */}
      {projects.length === 0 && (
        <Glass style={{ padding: 48, textAlign: 'center' }} hover={false}>
          <FolderKanban size={32} color={T.amber} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, color: T.textMid, fontWeight: 500 }}>No projects yet.</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 6 }}>Create your first project to get started.</div>
        </Glass>
      )}

      {/* List View */}
      {viewMode === 'list' && filtered.length > 0 && (
        <Glass style={{ padding: 0, overflow: 'hidden' }} hover={false}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 90px 80px 90px 100px 60px',
            gap: 8, padding: '10px 14px',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}>
            {['Name', 'Status', 'Priority', 'Workspace', 'Progress', 'Stage'].map(h => (
              <span key={h} style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</span>
            ))}
          </div>
          <AnimatePresence>
            {filtered.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02, ...springs.gentle }}>
                <ProjectRow project={p} milestones={milestones} tasks={tasks} onClick={() => onSelectProject(p)} />
              </motion.div>
            ))}
          </AnimatePresence>
        </Glass>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, alignItems: 'start' }}>
          {[
            { key: 'active', label: 'Active', items: activeProjects, color: T.green },
            { key: 'on_hold', label: 'On Hold', items: onHoldProjects, color: T.amber },
            { key: 'completed', label: 'Completed', items: completedProjects, color: T.blue },
          ].map(col => (
            <div key={col.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: col.color }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: col.color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {col.label}
                </span>
                <span style={{ fontSize: 11, color: T.textMuted }}>{col.items.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <AnimatePresence>
                  {col.items.map((p, i) => (
                    <motion.div key={p.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03, ...springs.gentle }}>
                      <ProjectCard project={p} milestones={milestones} tasks={tasks} onClick={() => onSelectProject(p)} />
                    </motion.div>
                  ))}
                </AnimatePresence>
                {col.items.length === 0 && (
                  <div style={{
                    padding: '24px 16px', borderRadius: 12, textAlign: 'center',
                    border: '2px dashed rgba(0,0,0,0.06)', color: T.textLight, fontSize: 12,
                  }}>
                    No projects
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Project Generator */}
      <AnimatePresence>
        {showAIGenerator && (
          <AIProjectGenerator
            roles={roles}
            onClose={() => setShowAIGenerator(false)}
            onCreated={onUpdate}
          />
        )}
      </AnimatePresence>

      {/* New Project Modal */}
      <AnimatePresence>
        {showNewProject && (
          <NewProjectModal
            roles={roles}
            templates={goals.filter(g => g.is_project && g.project_type === 'template')}
            milestones={milestones}
            tasks={tasks}
            onClose={() => setShowNewProject(false)}
            onCreated={onUpdate}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
